import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, Star, Search as SearchIcon, ArrowUpRight } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import { getInsights } from "@/lib/api";
import { useSEO } from "@/lib/seo";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch { return ""; }
}

export default function InsightsIndex() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");

  useEffect(() => {
    getInsights({ limit: 30 }).then((d) => setItems(d.items || [])).catch(() => {});
  }, []);

  const categories = useMemo(() => {
    const set = new Set();
    items.forEach((i) => set.add(i.category));
    return Array.from(set);
  }, [items]);

  const filtered = items.filter((i) => {
    if (category && i.category !== category) return false;
    if (q && !`${i.title} ${i.excerpt} ${(i.tags || []).join(" ")}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  useSEO({
    title: "Insights — Field Notes & Playbooks | RockLadder",
    description: "Cybersecurity playbooks, adversary analysis, and hard-won field notes from RockLadder's operators and analysts.",
    canonical: "https://rockladder.com/insights",
  });

  return (
    <>
      <PageHero
        eyebrow="INSIGHTS"
        title="Field notes,"
        gradient="from the front line."
        subtitle="Playbooks, adversary analyses, and post-incident lessons — written by the operators who lived them."
        breadcrumb={[{ label: "Insights" }]}
      />

      <section className="relative py-12 md:py-16 bg-white">
        <div className="mx-auto max-w-[1280px] px-6 md:px-10">
          {/* Filters */}
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-12 pb-6 border-b border-[rgba(11,11,16,0.08)]">
            <div className="relative flex-1 max-w-md">
              <SearchIcon className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8B8B95]" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search insights…"
                data-testid="insights-search"
                className="w-full pl-8 pr-4 py-2 bg-transparent border-0 border-b border-[rgba(11,11,16,0.10)] text-[14.5px] text-[#0B0B10] placeholder-[#8B8B95] focus:outline-none focus:border-[#5B2A86] transition-colors"
              />
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <button
                onClick={() => setCategory("")}
                data-testid="insights-category-all"
                className={`font-editorial-mono text-[10px] tracking-[0.28em] transition-colors ${
                  category === "" ? "text-[#5B2A86]" : "text-[#8B8B95] hover:text-[#0B0B10]"
                }`}
              >
                ALL
              </button>
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  data-testid={`insights-category-${c.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`font-editorial-mono text-[10px] tracking-[0.28em] transition-colors ${
                    category === c ? "text-[#5B2A86]" : "text-[#8B8B95] hover:text-[#0B0B10]"
                  }`}
                >
                  {c.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 md:gap-x-10 gap-y-12 md:gap-y-16">
            {filtered.map((it, i) => (
              <motion.div
                key={it.slug}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1], delay: (i % 3) * 0.06 }}
              >
                <Link
                  to={`/insights/${it.slug}`}
                  data-testid={`insights-card-${it.slug}`}
                  className="group block"
                >
                  {/* Editorial monogram cover */}
                  <div
                    className="relative overflow-hidden"
                    style={{
                      aspectRatio: "3 / 2",
                      background: "#F5F5F1",
                    }}
                  >
                    <div className="absolute inset-0 grid place-items-center">
                      <span
                        className="font-editorial-display leading-none"
                        style={{
                          fontStyle: "italic",
                          fontWeight: 400,
                          fontSize: "clamp(140px, 18vw, 220px)",
                          color: "rgba(11,11,16,0.20)",
                          fontVariationSettings: '"SOFT" 30, "opsz" 144',
                        }}
                      >
                        {(it.title || "").charAt(0)}
                      </span>
                    </div>

                    {/* Top-left label + featured */}
                    <div className="absolute top-4 left-4 flex flex-wrap items-center gap-3 font-editorial-mono text-[10px] tracking-[0.32em] text-[#8B8B95]">
                      <span>{it.category?.toUpperCase() || "INSIGHT"}</span>
                      {it.featured && (
                        <span className="inline-flex items-center gap-1 text-[#5B2A86]">
                          <Star className="h-2.5 w-2.5" strokeWidth={1.6} /> FEATURED
                        </span>
                      )}
                    </div>

                    {/* Bottom-right arrow */}
                    <div className="absolute bottom-4 right-4 text-[#5B2A86] transition-opacity duration-500 opacity-40 group-hover:opacity-100">
                      <ArrowUpRight className="h-5 w-5" strokeWidth={1.4} />
                    </div>

                    <span
                      aria-hidden
                      className="absolute bottom-0 left-0 h-[1.5px] bg-[#5B2A86] w-0 group-hover:w-full transition-[width] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
                    />
                  </div>

                  {/* Meta */}
                  <div className="pt-6">
                    <h3
                      className="font-editorial-display text-[#0B0B10] group-hover:text-[#5B2A86] transition-colors duration-300 line-clamp-2"
                      style={{
                        fontSize: 22,
                        lineHeight: 1.15,
                        letterSpacing: "-0.02em",
                        fontWeight: 400,
                      }}
                    >
                      {it.title}
                    </h3>
                    <p className="mt-3 text-[14.5px] leading-[1.55] text-[#4A4A55] line-clamp-2 max-w-[420px]">
                      {it.excerpt}
                    </p>
                    <div className="mt-5 flex items-center justify-between font-editorial-mono text-[10px] tracking-[0.28em] text-[#8B8B95]">
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="h-3 w-3" strokeWidth={1.6} />
                        {it.read_time_min} MIN
                      </span>
                      <span>{fmtDate(it.published_at)}</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20 text-[#4A4A55] text-sm">
              No insights match those filters.
            </div>
          )}
        </div>
      </section>
    </>
  );
}
